/**
 * Sealed product-story contract
 *
 * Route code describes owner chapters and served facts. Content that is not a
 * product-owned capability is a shared section of design-system elements. It
 * never injects JSX, supplies raw links/media URLs, or configures layout and
 * charts.
 */

import type { FmEvidenceDescriptor } from './FmEvidence.types';
import type { FmSection } from './sections/section.types';
import type { FmRegisteredProductMediaIdentity } from '@/shared/config/mediaRegistry';
import type { ContextualForestMediaRequest } from '@/shared/config/contextualForestMedia';

/**
 * Product-owned capabilities: governed proof media (image or film with its
 * transcript), the conversion action rail and the catalog offer. Every other
 * content is a shared section of elements.
 */
export const FM_PRODUCT_CAPABILITIES = [
  'registered-media',
  'contextual-actions',
  'catalog-decision-cta',
  'section',
] as const;

export type FmProductCapability = (typeof FM_PRODUCT_CAPABILITIES)[number];

export type FmProductSectionRole = 'lead' | 'evidence' | 'detail' | 'action';

export type FmProductConversionRole = 'orient' | 'build-trust' | 'compare' | 'convert' | 'handoff';

export interface FmProductChromeNavigationItem {
  id: string;
  label: string;
  chapterId: string;
}

export interface FmProductChromeLanguageOption {
  value: string;
  label: string;
}

export interface FmProductChromeLanguageControl {
  value: string;
  ariaLabel: string;
  options: readonly [FmProductChromeLanguageOption, ...FmProductChromeLanguageOption[]];
  onChange: (value: string) => void;
}

export interface FmProductChromeSignedOutIdentity {
  status: 'signed-out';
  loginLabel: string;
  registerLabel: string;
  onLogin: () => void;
  onRegister: () => void;
}

export interface FmProductChromeSignedInIdentity {
  status: 'signed-in';
  accountLabel: string;
  dashboardLabel: string;
  logoutLabel: string;
  onDashboard: () => void;
  onLogout: () => void;
}

export type FmProductChromeIdentity =
  | FmProductChromeSignedOutIdentity
  | FmProductChromeSignedInIdentity;

export interface FmProductChromeAuthDialog {
  open: boolean;
  mode: 'login' | 'register';
  title?: string;
  context?: string;
  closeLabel?: string;
  onClose: () => void;
  onAuthenticated: () => void;
}

export interface FmProductChromeDescriptor {
  brandLabel: string;
  onHome: () => void;
  navigationAriaLabel: string;
  menuAriaLabel: string;
  openMenuLabel: string;
  closeMenuLabel: string;
  navigation: readonly FmProductChromeNavigationItem[];
  language: FmProductChromeLanguageControl;
  identity: FmProductChromeIdentity;
  authDialog: FmProductChromeAuthDialog;
}

export interface FmProductFooterDescriptor {
  brandLabel: string;
  description: string;
  legalAriaLabel: string;
  termsLabel: string;
  privacyLabel: string;
  contactLabel: string;
  contactEmail: string;
  buyerLabel: string;
  onBuyer: () => void;
  allRightsReserved: string;
}

export interface FmProductEnabledAction {
  id: string;
  label: string;
  intent: 'primary' | 'secondary' | 'tertiary';
  onSelect: () => void;
  disabledReason?: never;
}

export interface FmProductDisabledAction {
  id: string;
  label: string;
  intent: 'primary' | 'secondary' | 'tertiary';
  disabledReason: string;
  onSelect?: never;
}

export type FmProductActionDescriptor = FmProductEnabledAction | FmProductDisabledAction;

export interface FmProductReadyState {
  status: 'ready';
}

export interface FmProductLoadingState {
  status: 'loading';
  label: string;
  detail?: string;
}

export interface FmProductEmptyState {
  status: 'empty';
  label: string;
  detail: string;
}

export interface FmProductPartialState {
  status: 'partial';
  label: string;
  detail: string;
}

export interface FmProductStaleState {
  status: 'stale';
  label: string;
  detail: string;
  updatedAt: string;
}

export interface FmProductLockedState {
  status: 'locked';
  label: string;
  detail: string;
  action: FmProductEnabledAction;
}

export interface FmProductErrorState {
  status: 'error';
  label: string;
  detail: string;
  retry?: FmProductEnabledAction;
}

export type FmProductAvailableState =
  | FmProductReadyState
  | FmProductPartialState
  | FmProductStaleState;

export type FmProductUnavailableState =
  | FmProductLoadingState
  | FmProductEmptyState
  | FmProductLockedState
  | FmProductErrorState;

export type FmProductSectionState = FmProductAvailableState | FmProductUnavailableState;

export interface FmProductHeroLeadInteraction {
  kind: 'search';
  purpose: 'property' | 'catalog';
  label: string;
  placeholder: string;
  value: string;
  submitLabel: string;
  helperText?: string;
  onValueChange: (value: string) => void;
  onSubmit: (value: string) => void;
}

export type FmProductHeroForecastMetric = 'growth' | 'harvest-income';
export type FmProductHeroSlide = 'growth' | 'choices' | 'harvest-income';

export interface FmProductHeroSlideCopy {
  title: string;
  summary: string;
  proofPoints: readonly [string, string, string];
}

export interface FmProductHeroPriceTrend {
  points: Array<Record<string, string | number | null>>;
  regionLabel: string;
  sourceLabel: string;
  pineLabel: string;
  pulpLabel: string;
}

export interface FmProductHeroForecastPoint {
  year: number;
  value: number;
}

export interface FmProductHeroForecastSeries {
  unit: string;
  points: readonly [FmProductHeroForecastPoint, ...FmProductHeroForecastPoint[]];
}

export interface FmProductHeroForecastAlternative {
  id: 'whole-period-value' | 'near-term-revenue' | 'avoid-clearcuts' | 'tapio';
  status: 'ready' | 'unavailable';
  firstOperationYear: number | null;
  npvPerHectareEur: number | null;
  netCashEur: number | null;
  unavailableReason?: 'revenue_now_signal_unavailable' | 'saved_plan_comparison_source_unavailable';
}

export interface FmProductHeroForecastCopy {
  propertyLabel: string;
  modelledLabel: string;
  growthLabel: string;
  harvestIncomeLabel: string;
  growthNavLabel: string;
  choicesNavLabel: string;
  choicesValueLabel: string;
  choicesNearTermLabel: string;
  choicesAvoidClearfellLabel: string;
  choicesTapioLabel: string;
  choicesPlanLabel: string;
  choicesNpvLabel: string;
  choicesNetLabel: string;
  choicesTableCaption: string;
  unavailableValueLabel: string;
  incomeNavLabel: string;
  loadingLabel: string;
  loadingDetail: string;
  unavailableLabel: string;
  unavailableDetail: string;
}

export interface FmProductHeroForecastLoading {
  status: 'loading';
  propertyId: string;
  copy: FmProductHeroForecastCopy;
}

export interface FmProductHeroForecastUnavailable {
  status: 'unavailable';
  propertyId: string;
  copy: FmProductHeroForecastCopy;
}

export interface FmProductHeroForecastReady {
  status: 'ready';
  propertyId: string;
  sourcePlanLabel: string;
  scopeLabel: string;
  displayLocale: string;
  copy: FmProductHeroForecastCopy;
  activeMetric: FmProductHeroForecastMetric;
  onMetricChange: (metric: FmProductHeroForecastMetric) => void;
  growth: FmProductHeroForecastSeries;
  harvestIncome: FmProductHeroForecastSeries;
  alternatives: readonly FmProductHeroForecastAlternative[];
}

/** A property-scoped model forecast, rendered by the product hero. */
export type FmProductHeroForecast =
  | FmProductHeroForecastLoading
  | FmProductHeroForecastUnavailable
  | FmProductHeroForecastReady;

export interface FmProductHeroDescriptor {
  id: string;
  state: FmProductSectionState;
  eyebrow?: string;
  title: string;
  summary: string;
  proofPoints: readonly [string, ...string[]];
  mediaIdentity: FmRegisteredProductMediaIdentity;
  /** Governed cinematic media paired with the three forecast story slides. */
  slideMediaIdentities?: Readonly<Record<FmProductHeroSlide, FmRegisteredProductMediaIdentity>>;
  slideCopy?: Readonly<Record<FmProductHeroSlide, FmProductHeroSlideCopy>>;
  carouselPlayLabel?: string;
  carouselPauseLabel?: string;
  priceTrend?: FmProductHeroPriceTrend;
  /** Optional governed visual proof layered over the cinematic hero background. */
  showcaseMediaIdentity?: FmRegisteredProductMediaIdentity;
  /** Visible context label when the showcase is an example rather than the active subject. */
  showcaseMediaLabel?: string;
  /** Optional modeled property forecast layered over the cinematic hero background. */
  forecast?: FmProductHeroForecast;
  /** Optional when the lead interaction itself is the hero's sole conversion action. */
  primaryAction?: FmProductEnabledAction;
  secondaryAction?: FmProductActionDescriptor;
  leadInteraction?: FmProductHeroLeadInteraction;
  nextSectionLabel: string;
}

interface FmProductSectionBase<K extends FmProductCapability> {
  id: string;
  capability: K;
  role: FmProductSectionRole;
  title: string;
  /** Shared reason-coded evidence presentation; never route-built badges or JSX. */
  evidence?: FmEvidenceDescriptor;
}

type FmAvailableProductSection<K extends FmProductCapability, Payload> = FmProductSectionBase<K> &
  Payload & {
    state: FmProductAvailableState;
  };

type FmUnavailableProductSection<K extends FmProductCapability> = FmProductSectionBase<K> & {
  state: FmProductUnavailableState;
};

export interface FmProductMediaFact {
  id: string;
  label: string;
  value: string | number;
  unit?: string;
}

interface FmRegisteredMediaCommonPayload {
  answer: string;
  /** One supporting sentence beneath the proof headline. */
  explanation?: string;
  caption: string;
  mediaIdentity: FmRegisteredProductMediaIdentity;
  /** Renderer-owned emphasis for product proof that must remain the visual focus. */
  presentation?: 'balanced' | 'balanced-media-first' | 'proof-dominant';
  /** Optional governed narrow capture selected at the mobile breakpoint. */
  mobileMediaIdentity?: FmRegisteredProductMediaIdentity;
  contextMedia?: ContextualForestMediaRequest;
  contextCaption?: string;
}

interface FmRegisteredImageMediaPayload extends FmRegisteredMediaCommonPayload {
  mediaKind?: 'image';
  facts: readonly [FmProductMediaFact, ...FmProductMediaFact[]];
}

export interface FmProductVideoTranscriptScene {
  id: string;
  timeLabel: string;
  title: string;
  detail: string;
}

interface FmRegisteredVideoMediaPayload extends FmRegisteredMediaCommonPayload {
  mediaKind: 'video';
  posterIdentity: FmRegisteredProductMediaIdentity;
  /** Optional reflowed narrow-layout master selected before src assignment. */
  mobileMediaIdentity?: FmRegisteredProductMediaIdentity;
  mobilePosterIdentity?: FmRegisteredProductMediaIdentity;
  playLabel: string;
  pauseLabel: string;
  transcriptLabel: string;
  transcriptTitle: string;
  transcriptScenes: readonly [FmProductVideoTranscriptScene, ...FmProductVideoTranscriptScene[]];
  facts?: never;
  contextMedia?: never;
  contextCaption?: never;
}

type FmRegisteredMediaPayload = FmRegisteredImageMediaPayload | FmRegisteredVideoMediaPayload;

export type FmProductRegisteredMediaSection =
  | FmAvailableProductSection<'registered-media', FmRegisteredMediaPayload>
  | FmUnavailableProductSection<'registered-media'>;

interface FmContextualActionsPayload {
  answer: string;
  context: string;
  actions: readonly [FmProductActionDescriptor, ...FmProductActionDescriptor[]];
}

export type FmProductContextualActionsSection =
  | FmAvailableProductSection<'contextual-actions', FmContextualActionsPayload>
  | FmUnavailableProductSection<'contextual-actions'>;

export interface FmProductCatalogDecisionRecord {
  recordId: string;
  catalogVersion?: string;
  offerLabel: string;
  priceLabel: string;
  priceStatus: 'available' | 'unavailable';
  prominence: 'primary' | 'secondary';
  audience?: 'owner' | 'transaction';
  groupLabel?: string;
  inheritanceLabel?: string;
  scopeLabel?: string;
  billingLabel: string;
  entitlementLabel: string;
  includes: readonly [string, ...string[]];
  relationship?: {
    label: string;
    targetRecordId: string;
    targetLabel: string;
  };
}

interface FmCatalogDecisionCtaPayload {
  answer: string;
  catalog: FmProductCatalogDecisionRecord;
  primaryAction: FmProductActionDescriptor;
  secondaryAction?: FmProductActionDescriptor;
}

export type FmProductCatalogDecisionCtaSection =
  | FmAvailableProductSection<'catalog-decision-cta', FmCatalogDecisionCtaPayload>
  | FmUnavailableProductSection<'catalog-decision-cta'>;

/**
 * Shared section content composed from the design-system elements. The section
 * carries its own state; the product section only names it for the chapter
 * (compact disclosure label, reading role).
 */
export interface FmProductSharedSection {
  id: string;
  capability: 'section';
  role: FmProductSectionRole;
  title: string;
  section: FmSection;
}

export type FmProductSectionDescriptor =
  | FmProductRegisteredMediaSection
  | FmProductContextualActionsSection
  | FmProductCatalogDecisionCtaSection
  | FmProductSharedSection;

export interface FmProductChapterTransition {
  label: string;
  targetChapterId?: string;
}

export interface FmProductChapterDescriptor {
  id: string;
  ownerQuestion: string;
  literalAnswer: string;
  /** The section that carries the chapter's dominant beat. */
  dominantSectionId: string;
  transition: FmProductChapterTransition;
  mobileOrder: number;
  conversionRole: FmProductConversionRole;
  sections: readonly [FmProductSectionDescriptor, ...FmProductSectionDescriptor[]];
}
